import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Plus, Loader2, Trash2, Pencil, Download, Search, RotateCcw, FileText, Users, Building2, DollarSign, ArrowRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/page-header";
import { insertPmTeamMemberSchema, type PmTeamMember, type AuditLog } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { z } from "zod";
import { format, formatDistanceToNow, isWithinInterval, subHours } from "date-fns";

type TeamMemberFormData = z.infer<typeof insertPmTeamMemberSchema>;

// Types for audit log filtering
interface AuditLogFilters {
  action: string;
  entityType: string;
  search: string;
  dateRange: string;
}

// Extended audit log with client group names
interface AuditLogWithNames extends AuditLog {
  oldClientGroupName?: string;
  newClientGroupName?: string;
}

interface TfnRedactionOperationsReference {
  jobId: string;
  organisationId: string;
  status: "quarantined" | "submission_unknown";
  provider: "claude_vision";
  providerFileId: string | null;
  providerRunId: string | null;
  attemptCount: number;
  safeReasonCode: string | null;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  submissionStartedAt: string | null;
  submissionUnknownAt: string | null;
  providerAcknowledgedAt: string | null;
}

export default function Settings() {
  const { toast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<PmTeamMember | undefined>();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<PmTeamMember | undefined>();
  const [showUndoDialog, setShowUndoDialog] = useState(false);
  const [migrationToUndo, setMigrationToUndo] = useState<string | null>(null);
  
  // Audit log filters
  const [auditFilters, setAuditFilters] = useState<AuditLogFilters>({
    action: "all",
    entityType: "all",
    search: "",
    dateRange: "all",
  });

  // Team members query
  const { data: teamMembers = [], isLoading: isLoadingMembers, isError: isMembersError, error: membersError } = useQuery<PmTeamMember[]>({
    queryKey: ["/api/project-manager/team-members"],
  });

  // Audit logs query - fetch all logs, filter client-side for responsive UX
  const { data: auditLogs = [], isLoading: isLoadingLogs, isError: isLogsError, error: logsError } = useQuery<AuditLogWithNames[]>({
    queryKey: ["/api/admin/audit-logs"],
  });

  const {
    data: tfnOperations = { jobs: [] },
    isLoading: isLoadingTfnOperations,
    isError: isTfnOperationsError,
  } = useQuery<{ jobs: TfnRedactionOperationsReference[] }>({
    queryKey: ["/api/admin/tfn-redaction-operations"],
    refetchInterval: 30_000,
  });

  const form = useForm<TeamMemberFormData>({
    resolver: zodResolver(insertPmTeamMemberSchema),
    defaultValues: {
      memberName: "",
      email: "",
      role: "",
      isActive: 1,
    },
  });

  const isEdit = !!selectedMember;

  const defaultValues = {
    memberName: "",
    email: "",
    role: "",
    isActive: 1,
  };

  const normalizeForForm = (member: PmTeamMember): TeamMemberFormData => ({
    memberName: member.memberName,
    email: member.email || "",
    role: member.role || "",
    isActive: member.isActive,
  });

  useEffect(() => {
    if (isModalOpen) {
      if (selectedMember) {
        form.reset(normalizeForForm(selectedMember));
      } else {
        form.reset(defaultValues);
      }
    }
  }, [isModalOpen, selectedMember, form]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset(defaultValues);
      setSelectedMember(undefined);
    }
    setIsModalOpen(newOpen);
  };

  const createMutation = useMutation({
    mutationFn: (data: TeamMemberFormData) =>
      apiRequest("POST", "/api/project-manager/team-members", data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/project-manager/team-members"]
      });
      toast({
        title: "Success",
        description: "Team member created successfully",
      });
      handleOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create team member",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: TeamMemberFormData) =>
      apiRequest("PATCH", `/api/project-manager/team-members/${selectedMember?.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/project-manager/team-members"]
      });
      toast({
        title: "Success",
        description: "Team member updated successfully",
      });
      handleOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update team member",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/project-manager/team-members/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/project-manager/team-members"]
      });
      toast({
        title: "Success",
        description: "Team member deleted successfully",
      });
      setShowDeleteDialog(false);
      setMemberToDelete(undefined);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete team member",
        variant: "destructive",
      });
    },
  });

  const undoMigrationMutation = useMutation({
    mutationFn: (batchId: string) =>
      apiRequest("POST", `/api/admin/undo-migration/${batchId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/audit-logs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/client-groups"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/contacts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals"] });
      toast({
        title: "Migration Reversed",
        description: "The client group migration has been undone successfully",
      });
      setShowUndoDialog(false);
      setMigrationToUndo(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Undo Failed",
        description: error.message || "Failed to undo migration",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: TeamMemberFormData) => {
    const payload = {
      ...data,
      email: data.email || null,
      role: data.role || null,
      isActive: data.isActive ?? 1,
    };

    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleAddMember = () => {
    setSelectedMember(undefined);
    setIsModalOpen(true);
  };

  const handleEditMember = (member: PmTeamMember) => {
    setSelectedMember(member);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (member: PmTeamMember) => {
    setMemberToDelete(member);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    if (memberToDelete) {
      deleteMutation.mutate(memberToDelete.id);
    }
  };

  const handleUndoClick = (batchId: string) => {
    setMigrationToUndo(batchId);
    setShowUndoDialog(true);
  };

  const handleUndoConfirm = () => {
    if (migrationToUndo) {
      undoMigrationMutation.mutate(migrationToUndo);
    }
  };

  // Filter audit logs based on filters
  const filteredLogs = auditLogs.filter((log) => {
    // Action filter
    if (auditFilters.action !== "all" && log.action !== auditFilters.action) return false;
    // Entity type filter
    if (auditFilters.entityType !== "all" && log.entityType !== auditFilters.entityType) return false;
    // Search filter
    if (auditFilters.search) {
      const searchLower = auditFilters.search.toLowerCase();
      const matchesDescription = log.changeDescription?.toLowerCase().includes(searchLower);
      const matchesOldGroup = log.oldClientGroupName?.toLowerCase().includes(searchLower);
      const matchesNewGroup = log.newClientGroupName?.toLowerCase().includes(searchLower);
      if (!matchesDescription && !matchesOldGroup && !matchesNewGroup) return false;
    }
    // Date range filter
    if (auditFilters.dateRange !== "all") {
      const logDate = new Date(log.createdAt);
      const now = new Date();
      if (auditFilters.dateRange === "24h") {
        if (!isWithinInterval(logDate, { start: subHours(now, 24), end: now })) return false;
      } else if (auditFilters.dateRange === "7d") {
        if (!isWithinInterval(logDate, { start: subHours(now, 24 * 7), end: now })) return false;
      } else if (auditFilters.dateRange === "30d") {
        if (!isWithinInterval(logDate, { start: subHours(now, 24 * 30), end: now })) return false;
      }
    }
    return true;
  });

  // Get unique migration batches for undo functionality
  const migrationBatches = auditLogs
    .filter((log) => log.action === "client_group_migration" && log.migrationBatchId)
    .reduce((acc, log) => {
      if (!log.migrationBatchId) return acc;
      if (!acc[log.migrationBatchId]) {
        acc[log.migrationBatchId] = {
          batchId: log.migrationBatchId,
          createdAt: log.createdAt,
          recordCount: 0,
          oldClientGroupName: log.oldClientGroupName,
          newClientGroupName: log.newClientGroupName,
        };
      }
      acc[log.migrationBatchId].recordCount++;
      return acc;
    }, {} as Record<string, { batchId: string; createdAt: Date; recordCount: number; oldClientGroupName?: string; newClientGroupName?: string }>);

  // Check if migration is within 24 hours (can be undone)
  const canUndoMigration = (createdAt: Date) => {
    return isWithinInterval(new Date(createdAt), { start: subHours(new Date(), 24), end: new Date() });
  };

  // Export audit logs to CSV
  const handleExportCSV = () => {
    const headers = ["Date", "Action", "Entity Type", "Entity ID", "Description", "Old Client Group", "New Client Group"];
    const rows = filteredLogs.map((log) => [
      format(new Date(log.createdAt), "yyyy-MM-dd HH:mm:ss"),
      log.action,
      log.entityType,
      log.entityId,
      log.changeDescription || "",
      log.oldClientGroupName || "",
      log.newClientGroupName || "",
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `audit-logs-${format(new Date(), "yyyy-MM-dd")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Export Complete",
      description: `Exported ${filteredLogs.length} audit log entries to CSV`,
    });
  };

  const getActionBadgeVariant = (action: string) => {
    switch (action) {
      case "client_group_migration":
        return "default";
      case "entity_created":
        return "outline";
      case "entity_updated":
        return "secondary";
      case "entity_deleted":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case "client_group_migration":
        return "Migration";
      case "entity_created":
        return "Created";
      case "entity_updated":
        return "Updated";
      case "entity_deleted":
        return "Deleted";
      default:
        return action;
    }
  };

  const getEntityIcon = (entityType: string) => {
    switch (entityType) {
      case "contact":
        return <Users className="h-3 w-3" />;
      case "account":
        return <Building2 className="h-3 w-3" />;
      case "deal":
        return <DollarSign className="h-3 w-3" />;
      default:
        return <FileText className="h-3 w-3" />;
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title="Admin Portal"
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Admin Portal" }
        ]}
      />

      <main className="flex-1 overflow-auto bg-muted/20 p-6">
        <div className="mx-auto max-w-7xl">
          <Tabs defaultValue="audit-logs" className="w-full">
            <TabsList>
              <TabsTrigger value="audit-logs" data-testid="tab-audit-logs">
                Audit Logs
              </TabsTrigger>
              <TabsTrigger value="team-members" data-testid="tab-team-members">
                Team Members
              </TabsTrigger>
              <TabsTrigger value="tfn-redaction" data-testid="tab-tfn-redaction">
                TFN Redaction
              </TabsTrigger>
            </TabsList>

            {/* Audit Logs Tab */}
            <TabsContent value="audit-logs" className="mt-6 space-y-4">
              {/* Migration Undo Section */}
              {Object.values(migrationBatches).filter((batch) => canUndoMigration(batch.createdAt)).length > 0 && (
                <Card>
                  <CardHeader className="py-3">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <CardTitle className="text-base">Recent Migrations</CardTitle>
                        <CardDescription className="text-xs">
                          Migrations within the last 24 hours can be undone
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="py-2">
                    <div className="space-y-2">
                      {Object.values(migrationBatches)
                        .filter((batch) => canUndoMigration(batch.createdAt))
                        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                        .map((batch) => (
                          <div
                            key={batch.batchId}
                            className="flex items-center justify-between rounded-md border px-3 py-2 text-xs"
                            data-testid={`migration-batch-${batch.batchId}`}
                          >
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className="text-xs">
                                {batch.recordCount} records
                              </Badge>
                              <span className="text-muted-foreground">
                                {batch.oldClientGroupName}
                              </span>
                              <ArrowRight className="h-3 w-3 text-muted-foreground" />
                              <span className="font-medium">
                                {batch.newClientGroupName}
                              </span>
                              <span className="text-muted-foreground">
                                {formatDistanceToNow(new Date(batch.createdAt), { addSuffix: true })}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUndoClick(batch.batchId)}
                              data-testid={`button-undo-${batch.batchId}`}
                            >
                              <RotateCcw className="mr-1 h-3 w-3" />
                              Undo
                            </Button>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Filters */}
              <Card>
                <CardHeader className="py-3">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-base">Audit Log</CardTitle>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleExportCSV}
                      disabled={filteredLogs.length === 0}
                      data-testid="button-export-csv"
                    >
                      <Download className="mr-1 h-3 w-3" />
                      Export CSV
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by description, client group..."
                        className="pl-8 text-xs h-9"
                        value={auditFilters.search}
                        onChange={(e) => setAuditFilters({ ...auditFilters, search: e.target.value })}
                        data-testid="input-search-audit"
                      />
                    </div>
                    <Select
                      value={auditFilters.action}
                      onValueChange={(value) => setAuditFilters({ ...auditFilters, action: value })}
                    >
                      <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="select-action-filter">
                        <SelectValue placeholder="Action" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Actions</SelectItem>
                        <SelectItem value="client_group_migration">Migration</SelectItem>
                        <SelectItem value="entity_created">Created</SelectItem>
                        <SelectItem value="entity_updated">Updated</SelectItem>
                        <SelectItem value="entity_deleted">Deleted</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={auditFilters.entityType}
                      onValueChange={(value) => setAuditFilters({ ...auditFilters, entityType: value })}
                    >
                      <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="select-entity-filter">
                        <SelectValue placeholder="Entity Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="contact">Contact</SelectItem>
                        <SelectItem value="account">Account</SelectItem>
                        <SelectItem value="deal">Deal</SelectItem>
                        <SelectItem value="director">Director</SelectItem>
                        <SelectItem value="shareholder">Shareholder</SelectItem>
                        <SelectItem value="trustee">Trustee</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={auditFilters.dateRange}
                      onValueChange={(value) => setAuditFilters({ ...auditFilters, dateRange: value })}
                    >
                      <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="select-date-filter">
                        <SelectValue placeholder="Date Range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Time</SelectItem>
                        <SelectItem value="24h">Last 24 Hours</SelectItem>
                        <SelectItem value="7d">Last 7 Days</SelectItem>
                        <SelectItem value="30d">Last 30 Days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {isLoadingLogs ? (
                    <div className="flex items-center justify-center p-8">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : isLogsError ? (
                    <div className="p-8 text-center">
                      <p className="text-destructive font-medium">Failed to load audit logs</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {logsError instanceof Error ? logsError.message : "An error occurred"}
                      </p>
                    </div>
                  ) : filteredLogs.length === 0 ? (
                    <div className="p-8 text-center">
                      <p className="text-muted-foreground">No audit logs found</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Audit logs will appear here when data changes are made
                      </p>
                    </div>
                  ) : (
                    <Table data-testid="table-audit-logs">
                      <TableHeader>
                        <TableRow className="text-xs">
                          <TableHead className="w-[140px]">Date</TableHead>
                          <TableHead className="w-[100px]">Action</TableHead>
                          <TableHead className="w-[100px]">Entity</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="w-[180px]">Migration</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredLogs.map((log) => (
                          <TableRow key={log.id} className="text-xs h-10" data-testid={`row-audit-${log.id}`}>
                            <TableCell className="text-muted-foreground">
                              {format(new Date(log.createdAt), "MMM d, yyyy HH:mm")}
                            </TableCell>
                            <TableCell>
                              <Badge variant={getActionBadgeVariant(log.action)} className="text-xs">
                                {getActionLabel(log.action)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5">
                                {getEntityIcon(log.entityType)}
                                <span className="capitalize">{log.entityType}</span>
                              </div>
                            </TableCell>
                            <TableCell className="max-w-[300px] truncate">
                              {log.changeDescription || "-"}
                            </TableCell>
                            <TableCell>
                              {log.action === "client_group_migration" && log.oldClientGroupName && log.newClientGroupName ? (
                                <div className="flex items-center gap-1 text-xs">
                                  <span className="text-muted-foreground truncate max-w-[60px]" title={log.oldClientGroupName}>
                                    {log.oldClientGroupName}
                                  </span>
                                  <ArrowRight className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                                  <span className="font-medium truncate max-w-[60px]" title={log.newClientGroupName}>
                                    {log.newClientGroupName}
                                  </span>
                                </div>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                  
                  {filteredLogs.length > 0 && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      Showing {filteredLogs.length} of {auditLogs.length} entries
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="tfn-redaction" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    TFN Redaction Operations
                  </CardTitle>
                  <CardDescription>
                    Reference-only jobs requiring reconciliation. Document
                    names, content and storage locations are never shown.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoadingTfnOperations ? (
                    <div className="flex items-center justify-center p-8">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : isTfnOperationsError ? (
                    <div className="p-8 text-center">
                      <p className="font-medium text-destructive">
                        TFN redaction operations are unavailable
                      </p>
                    </div>
                  ) : tfnOperations.jobs.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      No jobs require operational intervention.
                    </div>
                  ) : (
                    <Table data-testid="table-tfn-redaction-operations">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Job</TableHead>
                          <TableHead>Organisation</TableHead>
                          <TableHead>State</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead>Provider</TableHead>
                          <TableHead>Attempts</TableHead>
                          <TableHead>Updated</TableHead>
                          <TableHead>Expires</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tfnOperations.jobs.map((job) => (
                          <TableRow
                            key={job.jobId}
                            data-testid={`row-tfn-redaction-${job.jobId}`}
                          >
                            <TableCell className="font-mono text-xs">
                              {job.jobId}
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {job.organisationId}
                            </TableCell>
                            <TableCell>
                              <Badge variant="destructive">
                                {job.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs">
                              {job.safeReasonCode ?? "-"}
                            </TableCell>
                            <TableCell className="text-xs">
                              <div>{job.provider}</div>
                              <div className="font-mono text-muted-foreground">
                                {job.providerRunId ?? job.providerFileId ?? "-"}
                              </div>
                            </TableCell>
                            <TableCell>{job.attemptCount}</TableCell>
                            <TableCell className="text-xs">
                              {format(new Date(job.updatedAt), "MMM d, HH:mm")}
                            </TableCell>
                            <TableCell className="text-xs">
                              {job.expiresAt
                                ? format(new Date(job.expiresAt), "MMM d, HH:mm")
                                : "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Team Members Tab */}
            <TabsContent value="team-members" className="mt-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle>Team Members</CardTitle>
                    <Button onClick={handleAddMember} data-testid="button-add-team-member">
                      <Plus className="mr-2 h-4 w-4" />
                      Add Team Member
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {isLoadingMembers ? (
                    <div className="flex items-center justify-center p-8">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : isMembersError ? (
                    <div className="p-8 text-center">
                      <p className="text-destructive font-medium">Failed to load team members</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {membersError instanceof Error ? membersError.message : "An error occurred"}
                      </p>
                    </div>
                  ) : teamMembers.length === 0 ? (
                    <div className="p-8 text-center">
                      <p className="text-muted-foreground">No team members found</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Click "Add Team Member" to get started
                      </p>
                    </div>
                  ) : (
                    <Table data-testid="table-team-members">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {teamMembers.map((member) => (
                          <TableRow key={member.id} data-testid={`row-team-member-${member.id}`}>
                            <TableCell className="font-medium">{member.memberName}</TableCell>
                            <TableCell>{member.email || "-"}</TableCell>
                            <TableCell>{member.role || "-"}</TableCell>
                            <TableCell>
                              <Badge variant={member.isActive === 1 ? "default" : "secondary"}>
                                {member.isActive === 1 ? "Active" : "Inactive"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleEditMember(member)}
                                  data-testid={`button-edit-${member.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleDeleteClick(member)}
                                  data-testid={`button-delete-${member.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Team Member Modal */}
      <Dialog open={isModalOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{isEdit ? "Edit Team Member" : "Add Team Member"}</DialogTitle>
            <DialogDescription>
              {isEdit
                ? "Update the team member information below"
                : "Enter the details for the new team member"}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="memberName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Member Name *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., John Doe"
                        data-testid="input-member-name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="e.g., john.doe@example.com"
                        data-testid="input-email"
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                        ref={field.ref}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., Developer, Designer, Project Manager"
                        data-testid="input-role"
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                        ref={field.ref}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value === 1}
                        onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                        data-testid="checkbox-is-active"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Active</FormLabel>
                      <p className="text-sm text-muted-foreground">
                        Team member is currently active and available
                      </p>
                    </div>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <div className="flex w-full items-center justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOpenChange(false)}
                    disabled={isPending}
                    data-testid="button-cancel"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isPending} data-testid="button-save">
                    {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isEdit ? "Update Team Member" : "Create Team Member"}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Team Member Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the team member
              "{memberToDelete?.memberName}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-delete-confirm"
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Undo Migration Dialog */}
      <AlertDialog open={showUndoDialog} onOpenChange={setShowUndoDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Undo Migration?</AlertDialogTitle>
            <AlertDialogDescription>
              This will revert all records back to their original client groups. This action can be performed again if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-undo-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUndoConfirm}
              data-testid="button-undo-confirm"
            >
              {undoMigrationMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Undo Migration
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
